/**
 * Live QR scanning that decodes at the camera's native resolution.
 *
 * Both off-the-shelf scanners downscale before decoding — html5-qrcode to the
 * size of its on-screen viewfinder (~280px), qr-scanner to a fixed 400x400
 * (DEFAULT_CANVAS_SIZE). On Android that survives: the video is sharp enough
 * that a dense ticket code still resolves at 400px. On iOS it does not. Camera
 * *video* there is binned and denoised hard enough that the same code, which
 * looks perfectly sharp in the preview, is already too soft to read once it has
 * been squeezed to 400px. A full-resolution still of the identical code decodes
 * fine, which is what pins the downscale as the culprit rather than the lens,
 * the focus or the decoder.
 *
 * So: keep the video for the preview, where softness does not matter, and
 * decode a 1:1 crop instead of a shrunken one. Same live auto-scanning
 * behaviour, no taps, just without throwing away the pixels that carry the code.
 *
 * Passes alternate between a tight native-resolution centre crop (a code held
 * close, which is the common case at a door) and the whole frame capped on its
 * long side (a code held further back), so both distances get found without
 * doubling the work per pass.
 */

// The centre region qr-scanner uses, kept because it frames well on screen.
const CROP_FRACTION = 2 / 3;
// Cap for the wide pass. Big enough to keep module detail, small enough to
// decode inside a frame budget.
const WIDE_MAX = 1600;
const SCANS_PER_SECOND = 4;

/**
 * Which back lens, not just "a back lens".
 *
 * A multi-lens iPhone exposes several, and facingMode:environment can land on
 * any of them. Telephoto has a far longer minimum focus distance and simply
 * cannot hold a code held close to the phone — which reads as "works from a
 * distance, fails up close". Prefer the plain wide lens, take a virtual
 * dual/triple device next, then ultra-wide (softer, but focuses closest), and
 * only fall back to telephoto if it is all there is.
 */
export function pickBackCamera(cameras) {
  const back = (cameras || []).filter((c) => /back|rear|environment/i.test(c.label || ''));
  if (!back.length) return null;
  const rank = (c) => {
    const label = (c.label || '').toLowerCase();
    if (/telephoto/.test(label)) return 3;
    if (/ultra/.test(label)) return 2;
    if (/dual|triple/.test(label)) return 1;
    return 0;
  };
  return back.slice().sort((a, b) => rank(a) - rank(b))[0];
}

/** Ordered so the rear camera wins wherever the device lets it. */
function sourceLadder(cameras) {
  const rear = pickBackCamera(cameras);
  const last = cameras && cameras.length ? cameras[cameras.length - 1] : null;
  return [
    { facingMode: { exact: 'environment' } },
    rear ? { deviceId: { exact: rear.deviceId } } : null,
    { facingMode: { ideal: 'environment' } },
    last ? { deviceId: { exact: last.deviceId } } : null,
    { facingMode: 'user' },
  ].filter(Boolean);
}

export default class LiveTicketScanner {
  constructor(video, onResult) {
    this.video = video;
    this.onResult = onResult;
    this.stream = null;
    this.timer = null;
    this.running = false;
    this.widePass = false;
    this.canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  }

  async listCameras() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((d) => d.kind === 'videoinput');
    } catch {
      return [];
    }
  }

  /** Opens the first source that works, then starts decoding. */
  async start(preferredSource = null) {
    this.stop();

    // Labels are only populated once permission has been granted, so the first
    // run falls through to facingMode and later ones can match on the label.
    const cameras = await this.listCameras();
    const sources = preferredSource ? [preferredSource] : sourceLadder(cameras);

    let lastErr = null;
    for (const source of sources) {
      try {
        // eslint-disable-next-line no-await-in-loop
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { ...source, width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
        break;
      } catch (err) {
        lastErr = err;
      }
    }
    if (!this.stream) throw lastErr || new Error('no camera available');

    await this.tuneTrack();

    this.video.srcObject = this.stream;
    this.video.setAttribute('playsinline', 'true');
    this.video.muted = true;
    await this.video.play();

    this.running = true;
    this.scheduleNext(0);
    return this.activeDeviceId();
  }

  /**
   * Best-effort track tuning for close-range reading. Both of these are the
   * difference between a code held near the phone resolving or staying a blur,
   * and both are ignored where unsupported rather than failing the start.
   */
  async tuneTrack() {
    const track = this.stream && this.stream.getVideoTracks()[0];
    if (!track || typeof track.getCapabilities !== 'function') return;
    let caps = {};
    try { caps = track.getCapabilities() || {}; } catch { return; }

    const advanced = [];
    // A lens left zoomed in cannot focus close. Wind it back to its widest.
    if (caps.zoom && typeof caps.zoom.min === 'number') advanced.push({ zoom: caps.zoom.min });
    // Otherwise focus is locked wherever it happened to land when the stream
    // opened, so moving the ticket closer never refocuses.
    if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
      advanced.push({ focusMode: 'continuous' });
    }
    if (!advanced.length) return;
    try { await track.applyConstraints({ advanced }); } catch { /* best effort */ }
  }

  activeDeviceId() {
    const track = this.stream && this.stream.getVideoTracks()[0];
    return track ? track.getSettings().deviceId : null;
  }

  stop() {
    this.running = false;
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (this.stream) {
      // Release the hardware, or the camera indicator stays lit.
      this.stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
      this.stream = null;
    }
    if (this.video) { try { this.video.srcObject = null; } catch { /* noop */ } }
  }

  scheduleNext(delay = 1000 / SCANS_PER_SECOND) {
    if (!this.running) return;
    this.timer = setTimeout(() => this.tick(), delay);
  }

  /**
   * One decode pass. Self-pacing: the next is scheduled after this one
   * finishes, so a slow decode can never queue up behind itself.
   */
  async tick() {
    if (!this.running) return;
    const v = this.video;
    if (!v || !v.videoWidth || !v.videoHeight) { this.scheduleNext(); return; }

    try {
      const { default: QrScanner } = await import('qr-scanner');
      const ctx = this.canvas.getContext('2d', { willReadFrequently: true });

      let sx;
      let sy;
      let sw;
      let sh;
      let dw;
      let dh;
      if (this.widePass) {
        // Whole frame, capped on the long side — finds a code held further back.
        sx = 0; sy = 0; sw = v.videoWidth; sh = v.videoHeight;
        const scale = Math.min(1, WIDE_MAX / Math.max(sw, sh));
        dw = Math.round(sw * scale);
        dh = Math.round(sh * scale);
      } else {
        // Centre crop at 1:1. No downscale — this is the whole point.
        const side = Math.round(CROP_FRACTION * Math.min(v.videoWidth, v.videoHeight));
        sx = Math.round((v.videoWidth - side) / 2);
        sy = Math.round((v.videoHeight - side) / 2);
        sw = side; sh = side; dw = side; dh = side;
      }
      this.widePass = !this.widePass;

      if (this.canvas.width !== dw) this.canvas.width = dw;
      if (this.canvas.height !== dh) this.canvas.height = dh;
      // Smoothing blurs module edges, which is the opposite of what a decoder
      // wants.
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(v, sx, sy, sw, sh, 0, 0, dw, dh);

      // No scanRegion: passing one would reintroduce qr-scanner's downscale.
      const result = await QrScanner.scanImage(this.canvas, { returnDetailedScanResult: true });
      const text = typeof result === 'string' ? result : result && result.data;
      if (text && this.running) this.onResult(text);
    } catch {
      // No code in this frame — by far the common case, and not an error.
    }
    this.scheduleNext();
  }
}
